import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { confirmDialog } from "primereact/confirmdialog";

const initialForm = {
  jobOrderNo: "",
  deliveryDate: null,
};

const JONDdDialog = ({
  visible,
  onCancel,
  orderId,
  orderTotal = 0,
  toastrRef,
  onOrderAccepted,
  jonData,
  latestReferenceNo,
  isEdit = false,
}) => {
  const money = (v) =>
    (v || 0).toLocaleString("en-LK", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  const [form, setForm] = useState({ ...initialForm });

  useEffect(() => {
    if (visible) {
      if (jonData) {
        setForm({
          jobOrderNo: jonData.jobOrderNo || "",
          referenceNo: latestReferenceNo || "",
          deliveryDate: jonData.deliveryDate
            ? new Date(jonData.deliveryDate)
            : null,
        });
      } else {
        setForm({
          ...initialForm,
          referenceNo: latestReferenceNo || "",
        });
      }
    }
  }, [visible, jonData, latestReferenceNo]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!form.jobOrderNo || !form.deliveryDate || !form.referenceNo) {
      toastrRef.current.show({
        severity: "warn",
        summary: "Missing Fields",
        detail: "Please enter Job Order No, Bill No, and Delivery Date.",
        life: 3000,
      });
      return;
    }

    if (isEdit) {
      updateJON(orderId, form);
    } else {
      onCancel();
      confirmDialog({
        message: "Are you sure you want to accept this order?",
        header: "Confirmation",
        icon: "pi pi-exclamation-triangle",
        accept: () =>
          confirmAccept(
            orderId,
            form.jobOrderNo,
            form.referenceNo,
            form.deliveryDate,
            form.advanceAmount,
            form.roundOff
          ),
      });
    }
  };

  const confirmAccept = (
    orderId,
    jobOrderNo,
    referenceNo,
    deliveryDate,
    advanceAmount,
    roundOff
  ) => {
    const payload = {
      orderId,
      jobOrderNo,
      referenceNo,
      deliveryDate,
      advanceAmount: advanceAmount || 0,
      roundOff: roundOff || 0,
    };

    axios
      .post(`/api/orders/accept/${orderId}`, payload)
      .then((response) => {
        toastrRef.current.show({
          severity: "success",
          summary: "Quotation Accepted",
          detail: "Order has been accepted successfully.",
          life: 3000,
        });
        if (onOrderAccepted) {
          onOrderAccepted(response.data);
        }
        onCancel();
      })
      .catch((error) => {
        console.error("Error accepting quotation:", error);
        toastrRef.current.show({
          severity: "error",
          summary: "Error",
          detail: "An error occurred while accepting the quotation.",
          life: 3000,
        });
      });
  };

  const updateJON = (orderId, form) => {
    const payload = { ...form };
    axios
      .put(`/api/orders/update/${orderId}`, payload)
      .then((response) => {
        toastrRef.current.show({
          severity: "success",
          summary: "JON Updated",
          detail: "Job Order details updated successfully.",
          life: 3000,
        });
        if (onOrderAccepted) onOrderAccepted(response.data);
        onCancel();
      })
      .catch((error) => {
        console.error(error);
        toastrRef.current.show({
          severity: "error",
          summary: "Error",
          detail: "An error occurred while updating JON.",
          life: 3000,
        });
      });
  };

  return (
    <Dialog
      header="Add / Edit Job Order Details"
      visible={visible}
      onHide={onCancel}
      modal
      style={{ width: "400px" }}
    >
      <form onSubmit={handleSubmit}>
        <div className="p-fluid">
          <div className="p-field">
            <label htmlFor="referenceNo">Bill No*</label>
            <InputText
              id="referenceNo"
              name="referenceNo"
              value={form.referenceNo}
              onChange={(e) =>
                setForm({ ...form, referenceNo: e.target.value })
              }
              autoComplete="off"
              required
            />
          </div>
          <div className="p-field">
            <label htmlFor="jobOrderNo">Job Order No*</label>
            <InputText
              id="jobOrderNo"
              value={form.jobOrderNo}
              onChange={(e) => setForm({ ...form, jobOrderNo: e.target.value })}
              required
            />
          </div>
          <div className="p-field">
            <label htmlFor="deliveryDate">Delivery Date*</label>
            <Calendar
              id="deliveryDate"
              value={form.deliveryDate}
              onChange={(e) => setForm({ ...form, deliveryDate: e.value })}
              dateFormat="yy-mm-dd"
              showIcon
              required
              minDate={new Date()}
            />
          </div>
          {!isEdit && (
            <>
              <div className="p-field">
                <label>Order Total</label>
                <div style={{ fontWeight: 600 }}>LKR {money(orderTotal)}</div>
              </div>
              <div className="p-field">
                <label htmlFor="roundOff">Round Off</label>
                <InputNumber
                  id="roundOff"
                  value={form.roundOff}
                  onChange={(e) => setForm({ ...form, roundOff: e.value })}
                  onValueChange={(e) =>
                    setForm({ ...form, roundOff: e.value })
                  }
                  mode="decimal"
                  min={0}
                  minFractionDigits={0}
                  maxFractionDigits={2}
                  placeholder="0.00 (optional)"
                />
              </div>
              <div className="p-field">
                <label>Payable Total (after round off)</label>
                <div style={{ fontWeight: 700 }}>
                  LKR {money((orderTotal || 0) - (form.roundOff || 0))}
                </div>
              </div>
            </>
          )}
          {!isEdit && (
            <div className="p-field">
              <label htmlFor="advanceAmount">Advance Payment (LKR)</label>
              <InputNumber
                id="advanceAmount"
                value={form.advanceAmount}
                onValueChange={(e) =>
                  setForm({ ...form, advanceAmount: e.value })
                }
                mode="decimal"
                min={0}
                minFractionDigits={2}
                maxFractionDigits={2}
                placeholder="0.00 (optional)"
              />
            </div>
          )}
        </div>
        <div className="flex justify-content-end">
          <Button
            type="button"
            label="Cancel"
            className="p-button-text mr-2"
            onClick={onCancel}
          />
          <Button label="Submit" type="submit" />
        </div>
      </form>
    </Dialog>
  );
};

export default JONDdDialog;
